import React, { memo, useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectItem,
  Slider,
} from "@nextui-org/react";
import { useInfiniteScroll } from "@nextui-org/use-infinite-scroll";
import { useCategoryList } from "@/helpers/hooks/useCategoryList";
import classNames from "classnames";
import { useGenreList } from "@/helpers/hooks/useGenreList";
import { useCountryList } from "@/helpers/hooks/useCountryList";
import { IoCloseCircle } from "react-icons/io5";
import {
  ageRangeFilters,
  contentTypeFilters,
  languageFilters,
  sortingFilters,
} from "@/config/enums/filter.enum";
import { FiltersContext } from "@/screens/search/filtersContext";
import moment from "moment-jalaali";

export default function CategoryFilter() {
  const [selected, setSelected] = React.useState(new Set([]));
  const { isMobile, setCategory } = useContext(FiltersContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const { items, hasMore, isLoading, onLoadMore } = useCategoryList({
    fetchDelay: 1500,
  });

  useEffect(() => {
    setCategory(Array.from(selected).join());
  }, [selected]);

  const [, scrollerRef] = useInfiniteScroll({
    hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false, // We don't want to show the loader at the bottom of the list
    onLoadMore,
  });

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      isLoading={isLoading}
      items={items}
      label="دسته بندی"
      scrollRef={scrollerRef}
      selectionMode="single"
      radius="full"
      size="md"
      selectedKeys={selected}
      onSelectionChange={setSelected}
      labelPlacement={isMobile ? "inside" : "outside"}
      onOpenChange={setIsOpen}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => {
              setSelected(new Set([]));
            }}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        shouldBlockScroll: true,
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.slug}>
          {item.name}
        </SelectItem>
      )}
    </Select>
  );
}

export function GenreFilter() {
  const [selected, setSelected] = React.useState(new Set([]));
  const { isMobile, setGenre } = useContext(FiltersContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const { items, hasMore, isLoading, onLoadMore } = useGenreList({
    fetchDelay: 1500,
  });

  useEffect(() => {
    setGenre(Array.from(selected).join());
  }, [selected]);

  const [, scrollerRef] = useInfiniteScroll({
    hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false, // We don't want to show the loader at the bottom of the list
    onLoadMore,
  });

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      isLoading={isLoading}
      items={items}
      label="ژانر"
      scrollRef={scrollerRef}
      radius="full"
      size="md"
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      onOpenChange={setIsOpen}
      selectedKeys={selected}
      onSelectionChange={setSelected}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        shouldBlockScroll: true,
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.slug}>
          {item.name}
        </SelectItem>
      )}
    </Select>
  );
}

export function CountryFilter() {
  const { isMobile, setCountry } = useContext(FiltersContext);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(new Set([]));
  const { items, hasMore, isLoading, onLoadMore } = useCountryList();

  useEffect(() => {
    setCountry(Array.from(selected).toString());
  }, [selected]);

  const [, scrollerRef] = useInfiniteScroll({
    hasMore,
    isEnabled: isOpen,
    shouldUseLoader: false, // We don't want to show the loader at the bottom of the list
    onLoadMore,
  });

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      isLoading={isLoading}
      items={items}
      label="کشور سازنده"
      scrollRef={scrollerRef}
      radius="full"
      size="md"
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      onOpenChange={setIsOpen}
      selectedKeys={selected}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      onSelectionChange={setSelected}
      popoverProps={{
        showArrow: true,
        offset: 15,
        shouldBlockScroll: true,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.key}>
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
}

const YearFilterComp = () => {
  const JALALI = 1;
  const GEORGIAN = 2;

  const calendarConfig = {
    1: {
      min: 1300,
      max: moment().jYear(),
      defaultValue: [1370, moment().jYear()],
      marks: [
        {
          value: 1300,
          label: "1300",
        },
        {
          value: parseInt(Number(1300 + moment().jYear()) / 2),
          label: parseInt(Number(1300 + moment().jYear()) / 2).toString(),
        },
        {
          value: moment().jYear(),
          label: moment().jYear().toString(),
        },
      ],
    },
    2: {
      min: 1920,
      max: moment().year(),
      defaultValue: [1950, moment().year()],
      marks: [
        {
          value: 1920,
          label: "1920",
        },
        {
          value: parseInt(Number(1920 + moment().year()) / 2),
          label: parseInt(Number(1920 + moment().year()) / 2).toString(),
        },
        {
          value: moment().year(),
          label: moment().year().toString(),
        },
      ],
    },
  };

  const { isMobile, setYear } = useContext(FiltersContext);
  const [type, setType] = useState(JALALI);
  const [value, setValue] = useState(calendarConfig[type].defaultValue);
  const [dirty, setDirty] = useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  // useEffect(() => {
  // if (dirty) {
  //   setYear(value);
  // } else {
  //   setYear([null, null]);
  // }
  // }, [value, dirty, setYear]);

  return (
    <Popover
      placement="buttom"
      showArrow
      size="lg"
      offset={15}
      className="min-w-[145px] flex-auto custom-popover-parent"
      fullWidth
      isOpen={isOpen}
      shouldBlockScroll
      onOpenChange={(open) => {
        setDirty(true);
        setIsOpen(open);
      }}
    >
      <PopoverTrigger>
        <Input
          label="سال تولید"
          radius="full"
          readOnly
          className="min-w-[145px] flex-auto"
          fullWidth
          value={dirty ? `از ${value[0]} تا ${value[1]}` : ``}
          isClearable
          onClear={() => {
            setValue(calendarConfig[type].defaultValue);
            setYear([undefined, undefined]);
            setDirty(false);
          }}
          size="md"
          labelPlacement={isMobile ? "inside" : "outside"}
        />
      </PopoverTrigger>
      <PopoverContent className="p-5 w-[300px] border-1 custom-popover bg-[#262626]">
        <div className="flex mx-auto rounded-2xl overflow-hidden border-2 my-5">
          <Button
            className={classNames(
              type === JALALI ? "bg-white text-primary" : "bg-transparent"
            )}
            onClick={() => {
              setType(JALALI);
              setValue(calendarConfig[JALALI].defaultValue);
            }}
            radius="none"
          >
            شمسی
          </Button>
          <Button
            onClick={() => {
              setType(GEORGIAN);
              setValue(calendarConfig[GEORGIAN].defaultValue);
            }}
            className={classNames(
              type === GEORGIAN ? "bg-white text-primary" : "bg-transparent"
            )}
            radius="none"
          >
            میلادی
          </Button>
        </div>
        <Slider
          dir="ltr"
          classNames={{
            base: "mt-3",
            thumb: "bg-white",
            track: "bg-[#D9D9D9]",
            labelWrapper: "flex flex-row-reverse mb-3",
          }}
          showTooltip={false}
          step={1}
          showOutline
          tooltipProps={{
            size: "lg",
            className: "bg-white text-primary",
          }}
          size="sm"
          value={value}
          onChange={setValue}
          getValue={(val) => `از ${val[0]} تا ${val[1]}`}
          maxValue={calendarConfig[type].max}
          minValue={calendarConfig[type].min}
          marks={calendarConfig[type].marks}
          defaultValue={calendarConfig[type].defaultValue}
          className="max-w-md"
        />
        <Button
          fullWidth
          className="mt-5 text-white"
          color="primary"
          onClick={() => {
            setIsOpen(false);
            if (dirty) {
              const formattedValue = type === JALALI ? [
                moment(`${value[0]}/01/01`, 'jYYYY/jMM/jDD').year(),
                moment(`${value[1]}/12/29`, 'jYYYY/jMM/jDD').year()
              ] : value;
              setYear(formattedValue);
            } else {
              setYear([null, null]);
            }
          }}
        >
          تائید
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export const YearFilter = memo(YearFilterComp);

const IMDBFilterComp = () => {
  const { isMobile, setIMDB } = useContext(FiltersContext);
  const [value, setValue] = useState([7, 9]);
  const [dirty, setDirty] = useState(false);
  const [isOpen, setIsOpen] = React.useState(false);

  // useEffect(() => {
  //   if (dirty) {
  //     setIMDB(value);
  //   } else {
  //     setIMDB([null, null]);
  //   }
  // }, [value, dirty, setIMDB]);

  return (
    <Popover
      placement="buttom"
      showArrow
      size="lg"
      offset={15}
      className="min-w-[145px] flex-auto custom-popover-parent"
      fullWidth
      isOpen={isOpen}
      shouldBlockScroll
      onOpenChange={(open) => {
        setIsOpen(open);
        setDirty(true);
      }}
    >
      <PopoverTrigger>
        <Input
          label="امتیاز IMDB"
          radius="full"
          readOnly
          className="min-w-[145px] flex-auto"
          fullWidth
          value={dirty ? `از ${value[0]} تا ${value[1]}` : ""}
          isClearable
          labelPlacement={isMobile ? "inside" : "outside"}
          onClear={() => {
            setValue([7, 9]);
            setIMDB([undefined, undefined]);
            setDirty(false);
          }}
          size="md"
        />
      </PopoverTrigger>
      <PopoverContent className="p-5 w-96 custom-popover">
        <Slider
          dir="ltr"
          classNames={{
            base: "mt-3",
            thumb: "bg-white",
            labelWrapper: "flex flex-row-reverse mb-3",
            track: "bg-[#D9D9D9]",
          }}
          size="sm"
          showTooltip={false}
          step={1}
          showOutline
          tooltipProps={{
            size: "lg",
            className: "bg-white text-primary",
          }}
          value={value}
          onChange={setValue}
          getValue={(val) => `از ${val[0]} تا ${val[1]}`}
          maxValue={10}
          minValue={0}
          marks={[
            {
              value: 1,
              label: "1",
            },
            {
              value: 2,
              label: "2",
            },
            {
              value: 3,
              label: "3",
            },
            {
              value: 4,
              label: "4",
            },
            {
              value: 4,
              label: "4",
            },
            {
              value: 5,
              label: "5",
            },
            {
              value: 6,
              label: "6",
            },
            {
              value: 6,
              label: "6",
            },
            {
              value: 7,
              label: "7",
            },
            {
              value: 8,
              label: "8",
            },
            {
              value: 9,
              label: "9",
            },
            {
              value: 10,
              label: "10",
            },
          ]}
          defaultValue={[7, 9]}
          className="max-w-md"
        />
        <Button
          fullWidth
          className="mt-5 text-white"
          color="primary"
          onClick={() => {
            setIsOpen(false);
            if (dirty) {
              setIMDB(value);
            } else {
              setIMDB([null, null]);
            }
          }}
        >
          تائید
        </Button>
      </PopoverContent>
    </Popover>
  );
};

export const IMDBFilter = memo(IMDBFilterComp);

export const DurationFilter = () => {
  const { isMobile } = useContext(FiltersContext);
  const [value, setValue] = useState([60, 120]);
  const [dirty, setDirty] = useState(false);

  return (
    <Popover
      placement="buttom"
      showArrow
      size="lg"
      offset={15}
      className="min-w-[145px] flex-auto"
      fullWidth
      shouldBlockScroll
      onOpenChange={() => setDirty(true)}
    >
      <PopoverTrigger>
        <Input
          label="مدت زمان"
          radius="full"
          readOnly
          className="min-w-[145px] flex-auto"
          fullWidth
          value={dirty ? `از ${value[0]} تا ${value[1]}` : ""}
          isClearable
          labelPlacement={isMobile ? "inside" : "outside"}
          onClear={() => {
            setValue([60, 120]);
            setYear([undefined, undefined]);
            setDirty(false);
          }}
          size="md"
        />
      </PopoverTrigger>
      <PopoverContent className="p-5 w-[300px]">
        <Slider
          dir="ltr"
          classNames={{
            base: "mt-3",
            thumb: "bg-white",
            labelWrapper: "flex flex-row-reverse mb-3",
          }}
          showTooltip={false}
          step={1}
          showOutline
          tooltipProps={{
            size: "lg",
            className: "bg-white text-primary",
          }}
          value={value}
          onChange={setValue}
          getValue={(val) => `از ${val[0]} تا ${val[1]}`}
          maxValue={300}
          minValue={0}
          marks={[
            {
              value: 0,
              label: "0",
            },
            {
              value: 150,
              label: "150",
            },
            {
              value: 300,
              label: "300",
            },
          ]}
          defaultValue={[60, 120]}
          className="max-w-md"
        />
      </PopoverContent>
    </Popover>
  );
};

export const ContentTypeFilter = () => {
  const { isMobile, setKind } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState(new Set([]));

  useEffect(() => {
    setKind(Array.from(selected).join());
  }, [selected]);

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      items={contentTypeFilters}
      label="نوع محتوا"
      radius="full"
      size="md"
      shouldBlockScroll
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      selectedKeys={selected}
      onSelectionChange={(val) => {
        setSelected(val);
      }}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.key}>
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
};

export const LanguageFilter = () => {
  const { isMobile, setLanguage } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState(new Set([]));

  useEffect(() => {
    setLanguage(Array.from(selected).join());
  }, [selected]);

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      items={languageFilters}
      label="زبان"
      radius="full"
      size="md"
      shouldBlockScroll
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      selectedKeys={selected}
      onSelectionChange={setSelected}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.label}>
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
};

export const AgeRangeFilter = () => {
  const { isMobile, setContentAgeRange } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState(new Set([]));

  useEffect(() => {
    setContentAgeRange(Array.from(selected).join());
  }, [selected]);

  return (
    <Select
      className="min-w-[145px] flex-auto"
      fullWidth
      items={ageRangeFilters}
      label="رده سنی"
      radius="full"
      size="md"
      shouldBlockScroll
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      selectedKeys={selected}
      onSelectionChange={setSelected}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.key} value={item.key}>
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
};

export const SortingTypeFilter = () => {
  const { isMobile, setSortingType } = useContext(FiltersContext);
  const [selected, setSelected] = React.useState(new Set([]));

  useEffect(() => {
    setSortingType(Array.from(selected).join());
  }, [selected]);

  return (
    <Select
      className="min-w-[145px] flex-auto"
      items={sortingFilters}
      label="ترتیب نمایش"
      radius="full"
      size="md"
      fullWidth
      shouldBlockScroll
      labelPlacement={isMobile ? "inside" : "outside"}
      selectionMode="single"
      selectedKeys={selected}
      onSelectionChange={setSelected}
      endContent={
        selected.size > 0 && (
          <Button
            variant="light"
            radius="full"
            size="sm"
            style={{ marginLeft: -15 }}
            onClick={() => setSelected(new Set([]))}
            isIconOnly
          >
            <IoCloseCircle fill="#edeeef" color="#ecedee" size={20} />
          </Button>
        )
      }
      popoverProps={{
        showArrow: true,
        offset: 15,
        className: "custom-popover-parent",
        classNames: {
          content: "custom-popover px-1",
        },
      }}
    >
      {(item) => (
        <SelectItem className="p-3" key={item.key}>
          {item.label}
        </SelectItem>
      )}
    </Select>
  );
};
